import { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/Dialog';
import { useIncidentStore } from '../../../store/incidentStore';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';
import { Trash2 } from 'lucide-react';

const STATUSES = ['open', 'investigating', 'resolved'];

export function IncidentManagement() {
    const { incidents, metrics, fetchIncidents, fetchMetrics, createIncident, updateIncident, deleteIncident, loading } = useIncidentStore();
    const [isCreating, setIsCreating] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleStatusChange = async (id: string, status: string) => {
        try {
            await updateIncident(id, { status });
            toast.success(`Incident marked ${status}`);
            fetchMetrics();
        } catch {
            toast.error('Failed to update incident');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteIncident(deleteTarget.id);
            toast.success('Incident deleted');
            setDeleteTarget(null);
            fetchMetrics();
        } catch {
            toast.error('Failed to delete incident');
        } finally {
            setDeleting(false);
        }
    };
    const [newIncident, setNewIncident] = useState({
        title: '',
        description: '',
        severity: 'medium' as 'p0' | 'p1' | 'p2' | 'p3',
    });

    useEffect(() => {
        fetchIncidents();
        fetchMetrics();
    }, []);

    const severityColors: Record<string, string> = {
        p0: 'bg-destructive/20 text-destructive border-destructive/50',
        p1: 'bg-warning/20 text-warning border-warning/50',
        p2: 'bg-primary/20 text-primary border-primary/50',
        p3: 'bg-card text-muted-foreground border-border',
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Incident <span className="text-destructive">Response</span></h1>
                    <p className="text-muted-foreground mt-1">Managed crisis, outages, and platform incidents (P0-P3)</p>
                </div>
                <Button variant="primary" className="bg-destructive hover:bg-destructive/80 border-destructive/20" onClick={() => setIsCreating(true)}>Report Incident</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-destructive/10 border-destructive/20">
                    <CardContent className="p-6">
                        <p className="text-xs text-destructive font-semibold mb-1">Active P0/P1</p>
                        <p className="text-destructive font-display text-metric">{metrics?.activeCritical || 0}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <p className="text-xs text-muted-foreground font-semibold mb-1">Total MTTR</p>
                        <p className="text-foreground font-display text-metric">{metrics?.mttr || '0m'}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <p className="text-xs text-muted-foreground font-semibold mb-1">Uptime (30d)</p>
                        <p className="text-success font-display text-metric">{metrics?.uptime ?? 100}%</p>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="bg-card/30 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">Active Incidents</h2>
                </CardHeader>
                <CardContent className="p-0">
                    {loading && <div className="p-8 text-center text-muted-foreground">Loading incidents...</div>}
                    {!loading && incidents.length === 0 && (
                        <div className="p-12 text-center text-muted-foreground">No active incidents found. All systems operational.</div>
                    )}
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                <th className="text-left px-6 py-4">Severity</th>
                                <th className="text-left px-4 py-4">Title</th>
                                <th className="text-center px-4 py-4">Status</th>
                                <th className="text-center px-4 py-4">Created</th>
                                <th className="text-right px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-12">
                            {incidents.map((incident) => (
                                <tr key={incident.id} className="hover:bg-card/30 transition-colors">
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 rounded-lg border text-xs font-semibold ${severityColors[incident.severity]}`}>
                                            {incident.severity}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <p className="font-semibold text-foreground">{incident.title}</p>
                                        <p className="text-xs text-muted-foreground truncate max-w-md">{incident.description}</p>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <Badge variant={incident.status === 'open' ? 'error' : incident.status === 'resolved' ? 'success' : 'warning'}>{incident.status}</Badge>
                                    </td>
                                    <td className="px-4 py-4 text-center text-muted-foreground text-sm">{new Date(incident.createdAt).toLocaleString()}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <select
                                                value={incident.status}
                                                onChange={(e) => handleStatusChange(incident.id, e.target.value)}
                                                className="text-xs px-2 py-1 bg-background border border-border rounded-md text-foreground"
                                            >
                                                {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                                            </select>
                                            <Button variant="unstyled" size="unstyled"
                                                onClick={() => setDeleteTarget({ id: incident.id, title: incident.title })}
                                                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                                                aria-label="Delete incident"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Dialog open={isCreating} onOpenChange={setIsCreating}>
                <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Report Incident</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-muted-foreground font-semibold mb-2">Title</label>
                                <Input
                                    value={newIncident.title}
                                    onChange={(e) => setNewIncident(p => ({ ...p, title: e.target.value }))}
                                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-destructive font-semibold focus:outline-none focus:ring-2 focus:ring-destructive/50"
                                    placeholder="Brief summary of the issue"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-muted-foreground font-semibold mb-2">Severity</label>
                                <select
                                    value={newIncident.severity}
                                    onChange={(e) => setNewIncident(p => ({ ...p, severity: e.target.value as any }))}
                                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground font-semibold focus:outline-none focus:ring-2 focus:ring-destructive/50"
                                >
                                    <option value="p0">P0 - Critical Outage</option>
                                    <option value="p1">P1 - Major Functionality Hit</option>
                                    <option value="p2">P2 - Performance Degradation</option>
                                    <option value="p3">P3 - Minor Bug / Nuisance</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-muted-foreground font-semibold mb-2">Internal Notes</label>
                                <textarea
                                    value={newIncident.description}
                                    onChange={(e) => setNewIncident(p => ({ ...p, description: e.target.value }))}
                                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-destructive/50 resize-none"
                                    rows={4}
                                    placeholder="Provide root cause or observation..."
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="secondary" onClick={() => setIsCreating(false)}>Cancel</Button>
                                <Button variant="primary" className="bg-destructive border-destructive/10" onClick={async () => {
                                    if (!newIncident.title) {
                                      toast.error('Title is required');
                                      return;
                                    }
                                    try {
                                      await createIncident(newIncident);
                                      toast.success('Incident reported. Operations team notified.');
                                      setIsCreating(false);
                                      fetchIncidents();
                                    } catch (e) {
                                      toast.error('Failed to report incident');
                                    }
                                }}>Activate Incident</Button>
                            </div>
                        </div>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete incident</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2 pt-4">
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
